/**
 * navDev — developer harness for testing navmesh + movement WITHOUT a backend
 * bake (ENG-592). Injects a synthetic flat navmesh grid into the movement
 * component so you can see the ?nav=1 overlay and drive the avatar immediately.
 *
 * Console usage (with ?nav=1 on the URL):
 *   __NAV_DEV__.loadFlat()                       // 8m grid at the avatar's floor
 *   __NAV_DEV__.loadFlat({ size: 12, cell: 0.4 })
 *   __MOVEMENT__.moveToLocation([2, 0, 2])       // walk there
 *   __MOVEMENT__.moveToRandomPoint([0,0,0], 3)
 *   __MOVEMENT__.abort()
 *
 * Not a product surface — the real navmesh comes from the backend bake.
 */
import { NavMesh } from './navmesh'
import { movement } from './movementComponent'
// The grid generator moved to flatNavmesh.js when the studio floor started
// using it too — a component must not import this dev harness. Re-exported so
// the console API (and anything that imported it from here) is unchanged.
export { buildFlatNavmeshData } from './flatNavmesh'
import { buildFlatNavmeshData } from './flatNavmesh'

export function loadFlatNavmesh(opts = {}) {
  const floorY = opts.floorY ?? (typeof window !== 'undefined' && window.__AVATAR_FLOOR__?.getY?.()) ?? 0
  const nm = new NavMesh(buildFlatNavmeshData({ ...opts, floorY }))
  movement.setNavMesh(nm)
  if (typeof window !== 'undefined') {
    movement.setGroup(window.__AVATAR_GROUP__ || null)
    window.__NAVMESH_INSTANCE__ = nm
    window.__NAVMESH__ = { state: 'applied', nodeCount: nm.nodeCount, dev: true }
    // Tell NavMeshVolume to (re)build its debug overlay from the new graph.
    window.dispatchEvent(new CustomEvent('aos-navmesh-updated'))
  }
  console.log(`[navDev] injected flat navmesh: ${nm.nodeCount} nodes @ floorY=${floorY}. Try __MOVEMENT__.moveToLocation([2,${floorY},2])`)
  return nm
}

/**
 * Load a real baked navmesh.json from a URL into the live scene — for viewing a
 * locally-baked navmesh (npm run bake-navmesh) without a backend deploy:
 *   __NAV_DEV__.loadUrl('/assets/environments/navmesh/local.json')
 */
export async function loadNavmeshFromUrl(url) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`navmesh fetch ${res.status}: ${url}`)
  const nm = new NavMesh(await res.json())
  movement.setNavMesh(nm)
  if (typeof window !== 'undefined') {
    movement.setGroup(window.__AVATAR_GROUP__ || null)
    window.__NAVMESH_INSTANCE__ = nm
    window.__NAVMESH__ = { state: 'applied', nodeCount: nm.nodeCount, url }
    window.dispatchEvent(new CustomEvent('aos-navmesh-updated'))
  }
  console.log(`[navDev] loaded navmesh from ${url}: ${nm.nodeCount} nodes`)
  return nm
}

if (typeof window !== 'undefined') {
  window.__NAV_DEV__ = { loadFlat: loadFlatNavmesh, loadUrl: loadNavmeshFromUrl, buildFlatNavmeshData }
}
